import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Permission } from '@/types/global.types';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../stores/authStore';

interface RequirePermissionProps {
  permission: Permission | Permission[];
  mode?: 'any' | 'all';
  children: React.ReactNode;
  fallbackPath?: string;
}

/**
 * Route guard component that checks if the user has the required permission(s).
 * If not, redirects to the fallback path (default: dashboard).
 *
 * IMPORTANT: This component waits for permissions to be loaded before making
 * a redirect decision. This ensures that on page refresh, users stay on the
 * page if they have permission, rather than being incorrectly redirected.
 *
 * Usage:
 * <RequirePermission permission={Permission.NOTES_VIEW}>
 *   <GradesDashboardPage />
 * </RequirePermission>
 *
 * Or with multiple permissions:
 * <RequirePermission permission={[Permission.NOTES_VIEW, Permission.ATTENDANCE_VIEW]} mode="any">
 *   <SomePage />
 * </RequirePermission>
 */
export const RequirePermission: React.FC<RequirePermissionProps> = ({
  permission,
  mode = 'any',
  children,
  fallbackPath,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, permissionsLoaded } = usePermissions();
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  // Determine the fallback path based on user role
  const getDashboardPath = () => {
    if (fallbackPath) return fallbackPath;

    // Extract the base path from current location
    const pathParts = location.pathname.split('/');
    const institutionIndex = pathParts.findIndex(p => p === 'institution');
    if (institutionIndex !== -1 && pathParts[institutionIndex + 1]) {
      const tenantId = pathParts[institutionIndex + 1];
      const roleSegment = pathParts[institutionIndex + 2] || '';
      return `/institution/${tenantId}/${roleSegment}/dashboard`;
    }

    return '/';
  };

  // Check permission(s)
  const hasRequiredPermission = () => {
    if (Array.isArray(permission)) {
      return mode === 'all'
        ? hasAllPermissions(permission)
        : hasAnyPermission(permission);
    }
    return hasPermission(permission);
  };

  // Wait for permissions to be loaded before making redirect decision
  // This prevents incorrect redirects on page refresh while permissions are being fetched
  if (!permissionsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // If user doesn't have the required permission, redirect to dashboard
  if (!hasRequiredPermission()) {
    return <Navigate to={getDashboardPath()} replace />;
  }

  return <>{children}</>;
};

export default RequirePermission;
